import { useBillingSummary } from '@/features/billing/queries';
import { useOrgRole } from '@/features/settings/queries';
import { pausedReasonFor } from '@/features/billing/paused-copy';

export interface SubscriptionPausedState {
  /** True once `GET /billing/summary` has resolved with `status: 'PAUSED'`. */
  isPaused: boolean;
  /**
   * Why a control is disabled, ready to hand to a `disabledReason` /
   * tooltip. `null` when not paused, so a caller can write
   * `disabledReason = pausedReason ?? roleReason` and have the paused reason
   * win without a second condition.
   */
  reason: string | null;
}

/**
 * Whether the org's subscription is PAUSED, i.e. the whole panel is view-only.
 *
 * The BACKEND is the enforcement (`SubscriptionPausedInterceptor` → `040006`);
 * this hook is purely the UX — it disables the control and says why, so the
 * user doesn't press a button that can only fail.
 *
 * Reads `useBillingSummary()`, which is already mounted app-wide by the sidebar
 * plan card and the alert banner, so this adds no request. It is deliberately
 * NOT a route-level redirect: navigation stays open everywhere — a paused org
 * can still read every page its plan grants.
 *
 * Staleness: the summary has a 60s client `staleTime` on top of the server's
 * 45s cache, so a tab open when the pause lands can take ~105s to flip. The
 * central backstop in `lib/query-client.ts` invalidates the summary on the
 * first rejected write, which closes that window the moment it matters.
 */
export function useSubscriptionPaused(): SubscriptionPausedState {
  const { data } = useBillingSummary();
  const { isOrgOwner } = useOrgRole();

  const isPaused = data?.status === 'PAUSED';
  return {
    isPaused,
    reason: isPaused ? pausedReasonFor(isOrgOwner) : null,
  };
}
